import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Workflow, TeamMember, Asset, CreateAssignmentRequest, WorkflowWidgetData } from '../../types';
import { dashboardService, teamService } from '../../services';
import { dataSourceService, SecuritySearchResult } from '../../services/dataSourceService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortUp, faSortDown, faSort } from '@fortawesome/free-solid-svg-icons';
import Button from '../ui/Button';

interface NewAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateAssignmentRequest) => void;
  teamMembers: TeamMember[];
  workflows: Workflow[];
  assets: Asset[];
}

// Represents a widget that needs data at assignment time
interface WorkflowSourcedWidget {
  workflowId: string;
  workflowName: string;
  dashboardId: string;
  dashboardName: string;
  widgetId: string;
  widgetTitle: string;
  workflowDataKey: string;
  widgetType: string;
}

const NewAssignmentModal: React.FC<NewAssignmentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  teamMembers,
  workflows,
  assets,
}) => {
  const [selectedWorkers, setSelectedWorkers] = useState<string[]>([]);
  const [memberAssets, setMemberAssets] = useState<Record<string, string[]>>({});
  const [memberCoverageMap, setMemberCoverageMap] = useState<Record<string, Asset[]>>({});
  const [loadingCoverage, setLoadingCoverage] = useState(false);
  const [selectedWorkflows, setSelectedWorkflows] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState<string>('');
  const [eventDate, setEventDate] = useState<string>('');
  const [coverageFilter, setCoverageFilter] = useState<string>('all');
  const [coverageSearch, setCoverageSearch] = useState<string>('');
  const [step, setStep] = useState<number>(1);

  // Inline add asset state
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [addAssetQuery, setAddAssetQuery] = useState('');
  const [addAssetResults, setAddAssetResults] = useState<SecuritySearchResult[]>([]);
  const [addAssetSearching, setAddAssetSearching] = useState(false);
  const [addingAsset] = useState(false);
  const [localAssets, setLocalAssets] = useState<Asset[]>([]);
  const [removedAssetIds, setRemovedAssetIds] = useState<Set<string>>(new Set());
  const addAssetDropdownRef = useRef<HTMLDivElement>(null);

  // Review step state
  const [reviewSearch, setReviewSearch] = useState('');
  const [excludedRows, setExcludedRows] = useState<Set<number>>(new Set());
  const [reviewSort, setReviewSort] = useState<{ field: string; dir: 'asc' | 'desc' }>({ field: 'analyst', dir: 'asc' });

  // Sort + pagination state per step
  const PAGE_SIZE = 10;
  const [teamSort, setTeamSort] = useState<{ field: string; dir: 'asc' | 'desc' }>({ field: 'name', dir: 'asc' });
  const [teamPage, setTeamPage] = useState(0);
  const [coverageSort, setCoverageSort] = useState<{ field: string; dir: 'asc' | 'desc' }>({ field: 'name', dir: 'asc' });
  const [coveragePage, setCoveragePage] = useState(0);
  const [workflowSort, setWorkflowSort] = useState<{ field: string; dir: 'asc' | 'desc' }>({ field: 'name', dir: 'asc' });
  const [workflowPage, setWorkflowPage] = useState(0);
  const [reviewPage, setReviewPage] = useState(0);

  const toggleSortFor = (
    current: { field: string; dir: 'asc' | 'desc' },
    setter: React.Dispatch<React.SetStateAction<{ field: string; dir: 'asc' | 'desc' }>>,
    field: string,
  ) => {
    if (current.field === field) {
      setter({ field, dir: current.dir === 'asc' ? 'desc' : 'asc' });
    } else {
      setter({ field, dir: 'asc' });
    }
  };

  const SortTh: React.FC<{
    field: string;
    label: string;
    sort: { field: string; dir: 'asc' | 'desc' };
    onSort: (field: string) => void;
    className?: string;
  }> = ({ field, label, sort, onSort, className = '' }) => (
    <th
      className={`py-2.5 px-3 font-medium text-neutral-600 cursor-pointer select-none hover:text-neutral-800 transition-colors ${className}`}
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1.5">
        {label}
        <FontAwesomeIcon
          icon={sort.field === field ? (sort.dir === 'asc' ? faSortUp : faSortDown) : faSort}
          className={`w-3 h-3 ${sort.field === field ? 'text-primary-500' : 'text-neutral-300'}`}
        />
      </span>
    </th>
  );

  const Pagination: React.FC<{ total: number; page: number; setPage: (p: number) => void }> = ({ total, page, setPage: sp }) => {
    const totalPages = Math.ceil(total / PAGE_SIZE);
    if (totalPages <= 1) return null;
    return (
      <div className="flex items-center justify-between px-3 py-2 border-t border-neutral-200 text-xs text-neutral-500">
        <span>{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</span>
        <div className="flex gap-1">
          <button disabled={page === 0} onClick={() => sp(page - 1)} className="px-2 py-1 rounded border border-neutral-200 disabled:opacity-30 hover:bg-neutral-50">Prev</button>
          <button disabled={page >= totalPages - 1} onClick={() => sp(page + 1)} className="px-2 py-1 rounded border border-neutral-200 disabled:opacity-30 hover:bg-neutral-50">Next</button>
        </div>
      </div>
    );
  };

  // Widget data state
  const [, setWorkflowSourcedWidgets] = useState<WorkflowSourcedWidget[]>([]);
  const [widgetDataValues, setWidgetDataValues] = useState<Record<string, Record<string, string>>>({});
  const [, setLoadingWidgetInfo] = useState(false);

  // Fetch member coverage when workers are selected
  useEffect(() => {
    const fetchCoverage = async () => {
      if (selectedWorkers.length === 0) {
        setMemberCoverageMap({});
        setMemberAssets({});
        return;
      }
      setLoadingCoverage(true);
      try {
        const coverageMap: Record<string, Asset[]> = {};
        const assetMap: Record<string, string[]> = {};
        await Promise.all(
          selectedWorkers.map(async (workerId) => {
            try {
              const coverage = await teamService.getMemberCoverage(workerId);
              coverageMap[workerId] = coverage;
              assetMap[workerId] = coverage.map(a => a.id);
            } catch {
              coverageMap[workerId] = [];
              assetMap[workerId] = [];
            }
          })
        );
        setMemberCoverageMap(coverageMap);
        setMemberAssets(prev => {
          const updated: Record<string, string[]> = {};
          for (const workerId of selectedWorkers) {
            if (prev[workerId]) {
              // Preserve existing selections
              updated[workerId] = prev[workerId];
            } else {
              // Start with nothing selected — user picks in coverage step
              updated[workerId] = [];
            }
          }
          return updated;
        });
      } finally {
        setLoadingCoverage(false);
      }
    };
    fetchCoverage();
  }, [selectedWorkers]);

  // Calculate suggested due date based on selected workflows' expected duration
  useEffect(() => {
    if (selectedWorkflows.length === 0) return;

    const selectedWorkflowObjects = workflows.filter(w => selectedWorkflows.includes(w.id));
    const durations = selectedWorkflowObjects
      .map(w => w.expectedDurationDays)
      .filter((d): d is number => d !== undefined && d > 0);

    if (durations.length > 0) {
      const maxDuration = Math.max(...durations);
      const suggestedDate = new Date();
      suggestedDate.setDate(suggestedDate.getDate() + maxDuration);
      setDueDate(prev => prev || suggestedDate.toISOString().split('T')[0]);
    }
  }, [selectedWorkflows, workflows]);

  // Load workflow-sourced widgets when workflows are selected
  useEffect(() => {
    const loadWorkflowSourcedWidgets = async () => {
      if (selectedWorkflows.length === 0) {
        setWorkflowSourcedWidgets([]);
        return;
      }

      setLoadingWidgetInfo(true);
      const foundWidgets: WorkflowSourcedWidget[] = [];

      try {
        const selectedWorkflowObjects = workflows.filter(w => selectedWorkflows.includes(w.id));

        for (const workflow of selectedWorkflowObjects) {
          const dashboardIds = new Set<string>();
          workflow.flowData?.nodes?.forEach(node => {
            if (node.data?.dashboardId) {
              dashboardIds.add(node.data.dashboardId as string);
            }
          });

          for (const dashboardId of Array.from(dashboardIds)) {
            try {
              const dashboard = await dashboardService.getDashboard(dashboardId);

              dashboard.widgets?.forEach(widget => {
                if (widget.dataSourceConfig?.source === 'workflow' && widget.dataSourceConfig.workflowDataKey) {
                  foundWidgets.push({
                    workflowId: workflow.id,
                    workflowName: workflow.name,
                    dashboardId: dashboard.id,
                    dashboardName: dashboard.name,
                    widgetId: widget.id,
                    widgetTitle: widget.title || 'Untitled Widget',
                    workflowDataKey: widget.dataSourceConfig.workflowDataKey,
                    widgetType: 'onedrive',
                  });
                }
              });
            } catch (err) {
              console.error(`Failed to load dashboard ${dashboardId}:`, err);
            }
          }
        }

        setWorkflowSourcedWidgets(foundWidgets);

        setWidgetDataValues(prev => {
          const newValues = { ...prev };
          foundWidgets.forEach(w => {
            if (!newValues[w.workflowDataKey]) {
              newValues[w.workflowDataKey] = { fileId: '', fileName: '' };
            }
          });
          return newValues;
        });
      } catch (err) {
        console.error('Failed to load workflow widget info:', err);
      } finally {
        setLoadingWidgetInfo(false);
      }
    };

    loadWorkflowSourcedWidgets();
  }, [selectedWorkflows, workflows]);


  // Combined assets: system assets + any newly added ones
  const allAssets = useMemo(() => {
    const ids = new Set(assets.map(a => a.id));
    return [...assets, ...localAssets.filter(a => !ids.has(a.id))];
  }, [assets, localAssets]);

  // Asset-centric view: always show ALL system assets
  const assetView = useMemo(() => {
    const assetMap = new Map<string, { asset: Asset; coveringMembers: string[] }>();

    // 1. Add assets from selected members' coverage (these are mandatory, can't be removed)
    for (const [memberId, coverageAssets] of Object.entries(memberCoverageMap)) {
      for (const asset of coverageAssets) {
        const existing = assetMap.get(asset.id);
        if (existing) {
          existing.coveringMembers.push(memberId);
        } else {
          assetMap.set(asset.id, { asset, coveringMembers: [memberId] });
        }
      }
    }

    // 2. Add the manager's own coverage (the `assets` prop) — selectable, not
    //    tied to a member's existing coverage. Without this the step is empty
    //    until workers happen to already cover something.
    for (const asset of assets) {
      if (removedAssetIds.has(asset.id)) continue;
      if (!assetMap.has(asset.id)) {
        assetMap.set(asset.id, { asset, coveringMembers: [] });
      }
    }

    // 3. Add locally added assets (from "+ Add New"), minus any removed
    for (const asset of localAssets) {
      if (removedAssetIds.has(asset.id)) continue;
      if (!assetMap.has(asset.id)) {
        assetMap.set(asset.id, { asset, coveringMembers: [] });
      }
    }

    return assetMap;
  }, [memberCoverageMap, assets, localAssets, removedAssetIds]);

  // Unique categories (sectors) for filtering
  const coverageCategories = useMemo(() => {
    const cats = new Set<string>();
    assetView.forEach(({ asset }) => {
      if (asset.sector) cats.add(asset.sector);
    });
    return Array.from(cats).sort();
  }, [assetView]);

  // Filtered assets for the coverage step
  const filteredCoverageAssets = useMemo(() => {
    let entries = Array.from(assetView.entries());
    if (coverageFilter !== 'all') {
      entries = entries.filter(([_, { asset }]) => asset.sector === coverageFilter);
    }
    if (coverageSearch.trim()) {
      const q = coverageSearch.toLowerCase();
      entries = entries.filter(([_, { asset }]) =>
        asset.name.toLowerCase().includes(q) ||
        (asset.dataUid && asset.dataUid.toLowerCase().includes(q)) ||
        (asset.sector && asset.sector.toLowerCase().includes(q))
      );
    }
    return entries;
  }, [assetView, coverageFilter, coverageSearch]);

  // Flat lookup for review step
  const allCoverageAssets = useMemo(() => {
    const map = new Map<string, Asset>();
    assetView.forEach(({ asset }, id) => map.set(id, asset));
    return map;
  }, [assetView]);

  // Pre-compute excluded rows when entering review step
  // (kept before stepConfig declaration — uses isReviewRef to avoid ordering issues)
  const isReviewRef = useRef(false);
  useEffect(() => {
    // Will be updated by the render cycle; the effect runs after render when step changes
    if (!isReviewRef.current) return;

    const rows: Array<{ workerId: string; assetId?: string; workflowId: string }> = [];
    for (const workerId of selectedWorkers) {
      const workerAssetIds = memberAssets[workerId] || [];
      for (const workflowId of selectedWorkflows) {
        if (workerAssetIds.length > 0) {
          for (const assetId of workerAssetIds) {
            rows.push({ workerId, assetId, workflowId });
          }
        } else {
          rows.push({ workerId, workflowId });
        }
      }
    }

    const excluded = new Set<number>();
    rows.forEach((row, i) => {
      const workerCoverage = memberCoverageMap[row.workerId] || [];
      if (workerCoverage.length === 0) {
        excluded.add(i);
      } else if (row.assetId) {
        const coversThisAsset = workerCoverage.some(a => a.id === row.assetId);
        if (!coversThisAsset) {
          excluded.add(i);
        }
      }
    });
    setExcludedRows(excluded);
  }, [step, selectedWorkers, selectedWorkflows, memberAssets, memberCoverageMap]);

  if (!isOpen) return null;

  const toggleSelection = (
    id: string,
    selected: string[],
    setSelected: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    if (selected.includes(id)) {
      setSelected(selected.filter((s) => s !== id));
    } else {
      setSelected([...selected, id]);
    }
  };

  const isAssetEnabled = (assetId: string) => {
    // Check if ANY selected worker has this asset in their selections
    return selectedWorkers.some(wId => (memberAssets[wId] || []).includes(assetId));
  };

  const toggleAssetForAll = (assetId: string) => {
    const enabled = isAssetEnabled(assetId);
    setMemberAssets(prev => {
      const updated = { ...prev };
      for (const memberId of selectedWorkers) {
        const current = updated[memberId] || [];
        if (enabled) {
          // Disable: remove asset from all covering members
          updated[memberId] = current.filter(id => id !== assetId);
        } else {
          // Enable: add asset to all covering members
          if (!current.includes(assetId)) {
            updated[memberId] = [...current, assetId];
          }
        }
      }
      return updated;
    });
  };

  const handleSubmit = () => {
    // Validate event date if required
    if (needsEventDate && !eventDate) return;

    const widgetData: WorkflowWidgetData = {};
    Object.entries(widgetDataValues).forEach(([dataKey, values]) => {
      if (values.fileId || values.fileName) {
        widgetData[dataKey] = {
          fileId: values.fileId,
          fileName: values.fileName,
        };
      }
    });

    // Rebuild memberAssets from included rows only
    const allRows = getAssignmentRows();
    const includedRows = allRows.filter((_, i) => !excludedRows.has(i));
    const finalMemberAssets: Record<string, string[]> = {};
    const finalWorkerIds = new Set<string>();
    const finalWorkflowIds = new Set<string>();
    for (const row of includedRows) {
      finalWorkerIds.add(row.workerId);
      finalWorkflowIds.add(row.workflowId);
      if (row.assetId) {
        if (!finalMemberAssets[row.workerId]) finalMemberAssets[row.workerId] = [];
        if (!finalMemberAssets[row.workerId].includes(row.assetId)) {
          finalMemberAssets[row.workerId].push(row.assetId);
        }
      }
    }

    onSubmit({
      workerIds: Array.from(finalWorkerIds),
      workflowIds: Array.from(finalWorkflowIds),
      memberAssets: finalMemberAssets,
      dueDate: dueDate || undefined,
      eventDate: eventDate || undefined,
      widgetData: Object.keys(widgetData).length > 0 ? widgetData : undefined,
    });
    // Reset state
    setSelectedWorkers([]);
    setSelectedWorkflows([]);
    setMemberAssets({});
    setMemberCoverageMap({});
    setCoverageFilter('all');
    setCoverageSearch('');
    setDueDate('');
    setWidgetDataValues({});
    setWorkflowSourcedWidgets([]);
    setLocalAssets([]);
    setRemovedAssetIds(new Set());
    setAddAssetQuery('');
    setAddAssetResults([]);
    setReviewSearch('');
    setExcludedRows(new Set());
    setStep(1);
  };

  const canProceed = () => {
    switch (currentStepKey) {
      case 'workflows':
        return selectedWorkflows.length > 0;
      case 'team':
        return selectedWorkers.length > 0;
      case 'assets':
        return true;
      case 'widgetData':
        return true;
      case 'review':
        return !(needsEventDate && !eventDate);
      default:
        return false;
    }
  };

  // Whether any selected workflow requires an asset
  const selectedRequiresAsset = selectedWorkflows.some(wId => {
    const wf = workflows.find(w => w.id === wId);
    return wf?.requiresAsset;
  });

  // Whether any selected workflow needs a specific event date at assignment time
  const needsEventDate = selectedWorkflows.some(wId => {
    const wf = workflows.find(w => w.id === wId);
    return wf?.eventCategory === 'specific_date';
  });

  // Steps: Workflows -> Team -> [Coverage] -> [Widget Data] -> Review
  const getStepConfig = () => {
    const steps: { num: number; label: string; key: string }[] = [
      { num: 1, label: 'Workflows', key: 'workflows' },
      { num: 2, label: 'Team', key: 'team' },
    ];

    let nextNum = 3;

    if (selectedRequiresAsset) {
      steps.push({ num: nextNum++, label: 'Coverage', key: 'assets' });
    }

    steps.push({ num: nextNum, label: 'Review', key: 'review' });

    return steps;
  };

  const stepConfig = getStepConfig();

  // Build ordered list of step keys based on current config
  const stepKeys = stepConfig.map(s => s.key);

  const getCurrentStepKey = () => {
    return stepKeys[step - 1] || 'review';
  };

  const currentStepKey = getCurrentStepKey();
  const isReviewStep = currentStepKey === 'review';
  const isAssetsStep = currentStepKey === 'assets';
  isReviewRef.current = isReviewStep;

  const getDisplayStepNum = () => {
    const config = stepConfig.find(s => s.key === currentStepKey);
    return config?.num || 1;
  };

  const getNextStep = () => {
    return Math.min(step + 1, stepKeys.length);
  };

  const getPrevStep = () => {
    return Math.max(step - 1, 1);
  };

  const getAssignmentRows = () => {
    const rows: Array<{ workerId: string; assetId?: string; workflowId: string }> = [];
    for (const workerId of selectedWorkers) {
      const workerAssetIds = memberAssets[workerId] || [];
      for (const workflowId of selectedWorkflows) {
        if (workerAssetIds.length > 0) {
          for (const assetId of workerAssetIds) {
            rows.push({ workerId, assetId, workflowId });
          }
        } else {
          rows.push({ workerId, workflowId });
        }
      }
    }
    return rows;
  };


  const publishedWorkflows = workflows.filter(w => w.status === 'published');

  const toggleSelectAllWorkers = () => {
    if (selectedWorkers.length === teamMembers.length) {
      setSelectedWorkers([]);
    } else {
      setSelectedWorkers(teamMembers.map(m => m.id));
    }
  };

  const toggleSelectAllWorkflows = () => {
    if (selectedWorkflows.length === publishedWorkflows.length) {
      setSelectedWorkflows([]);
    } else {
      setSelectedWorkflows(publishedWorkflows.map(w => w.id));
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/70 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-surface rounded-xl shadow-2xl border border-neutral-200 max-w-4xl w-full mx-auto z-10">
          {/* Header */}
          <div className="px-6 py-4 border-b border-neutral-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-neutral-900">Create New Assignment</h2>
              <button
                onClick={onClose}
                className="text-neutral-400 hover:text-neutral-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* Progress Steps */}
            <div className="flex items-center mt-4 space-x-2">
              {stepConfig.map((s, i) => {
                const displayStepNum = getDisplayStepNum();
                const isCompleted = s.num < displayStepNum;
                const isCurrent = s.key === currentStepKey;
                return (
                  <React.Fragment key={s.key}>
                    <div
                      className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                        isCompleted || isCurrent
                          ? 'bg-primary-600 text-white'
                          : 'bg-neutral-200 text-neutral-600'
                      }`}
                    >
                      {s.num}
                    </div>
                    <span
                      className={`text-sm ${
                        isCompleted || isCurrent ? 'text-primary-600 font-medium' : 'text-neutral-500'
                      }`}
                    >
                      {s.label}
                    </span>
                    {i < stepConfig.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 ${
                          isCompleted ? 'bg-primary-600' : 'bg-neutral-200'
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 h-[60vh] overflow-y-auto">
            {/* Step 1: Select Team Members */}
            {currentStepKey === 'team' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-medium text-neutral-900">Select Team Members</h3>
                  {teamMembers.length > 0 && (
                    <button
                      onClick={toggleSelectAllWorkers}
                      className="h-7 px-2.5 text-xs font-medium text-neutral-700 bg-neutral-100 border border-neutral-200 rounded hover:bg-neutral-50 transition-colors"
                    >
                      {selectedWorkers.length === teamMembers.length ? 'Deselect All' : 'Select All'}
                    </button>
                  )}
                </div>
                <p className="text-sm text-neutral-500 mb-4">
                  Choose team members to assign workflows to
                </p>
                <div className="border border-neutral-200 rounded-lg overflow-hidden">
                  {(() => {
                    const sorted = [...teamMembers].sort((a, b) => {
                      let cmp = 0;
                      switch (teamSort.field) {
                        case 'name': cmp = a.fullName.localeCompare(b.fullName); break;
                        case 'email': cmp = a.email.localeCompare(b.email); break;
                        case 'coverage': cmp = (a.coverageCount ?? 0) - (b.coverageCount ?? 0); break;
                        case 'active': cmp = (a.activeAssignments ?? 0) - (b.activeAssignments ?? 0); break;
                      }
                      return teamSort.dir === 'asc' ? cmp : -cmp;
                    });
                    const paged = sorted.slice(teamPage * PAGE_SIZE, (teamPage + 1) * PAGE_SIZE);
                    return (
                      <>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-neutral-50 border-b border-neutral-200">
                        <th className="w-10 py-2.5 px-3">
                          <input
                            type="checkbox"
                            checked={selectedWorkers.length === teamMembers.length && teamMembers.length > 0}
                            onChange={toggleSelectAllWorkers}
                            className="w-4 h-4 rounded border-neutral-300 text-primary-600 cursor-pointer"
                          />
                        </th>
                        <SortTh field="name" label="Name" sort={teamSort} onSort={(f) => { toggleSortFor(teamSort, setTeamSort, f); setTeamPage(0); }} className="text-center" />
                        <SortTh field="email" label="Email" sort={teamSort} onSort={(f) => { toggleSortFor(teamSort, setTeamSort, f); setTeamPage(0); }} className="text-center" />
                        <SortTh field="coverage" label="Coverage" sort={teamSort} onSort={(f) => { toggleSortFor(teamSort, setTeamSort, f); setTeamPage(0); }} className="w-20 text-center" />
                        <SortTh field="active" label="Active" sort={teamSort} onSort={(f) => { toggleSortFor(teamSort, setTeamSort, f); setTeamPage(0); }} className="w-16 text-center" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                  {paged.map((member) => {
                    const selected = selectedWorkers.includes(member.id);
                    return (
                      <tr
                        key={member.id}
                        onClick={() => toggleSelection(member.id, selectedWorkers, setSelectedWorkers)}
                        className={`cursor-pointer transition-colors ${selected ? 'bg-primary-900/20' : 'hover:bg-neutral-50'}`}
                      >
                        <td className="w-10 py-3 px-3">
                          <input
                            type="checkbox"
                            checked={selected}
                            readOnly
                            className="w-4 h-4 rounded border-neutral-300 text-primary-600 shrink-0 cursor-pointer"
                          />
                        </td>
                        <td className="py-3 px-3 font-medium text-neutral-900">{member.fullName}</td>
                        <td className="py-3 px-3 text-neutral-500">{member.email}</td>
                        <td className="w-20 py-3 px-3 text-center text-neutral-400">
                          {member.coverageCount != null ? (member.coverageCount > 0 ? member.coverageCount : 'All') : '—'}
                        </td>
                        <td className="w-16 py-3 px-3 text-center text-neutral-400">
                          {member.activeAssignments ?? 0}
                        </td>
                      </tr>
                    );
                  })}
                    </tbody>
                  </table>
                  <Pagination total={sorted.length} page={teamPage} setPage={setTeamPage} />
                      </>
                    );
                  })()}
                </div>
                {teamMembers.length === 0 && (
                  <div className="text-center py-8 text-neutral-500">
                    No team members available
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Coverage — asset selection */}
            {isAssetsStep && (
              <div>
                <h3 className="text-lg font-medium text-neutral-900 mb-2">Coverage</h3>
                <p className="text-sm text-neutral-500 mb-4">
                  Select assets to include in the assignments. All selected team members will be assigned to checked assets.
                </p>

                {/* Add New Asset overlay */}
                {showAddAsset && (
                  <div className="fixed inset-0 z-[70] flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/50" onClick={() => { setShowAddAsset(false); setAddAssetQuery(''); setAddAssetResults([]); }} />
                    <div className="relative bg-surface rounded-xl shadow-2xl border border-neutral-200 w-full max-w-md p-5 z-10">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-neutral-900">Add New Asset</h4>
                        <button onClick={() => { setShowAddAsset(false); setAddAssetQuery(''); setAddAssetResults([]); }} className="text-neutral-400 hover:text-neutral-600">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <p className="text-sm text-neutral-500 mb-3">Search for a company or ticker to add to your coverage.</p>
                      <div className="relative" ref={addAssetDropdownRef}>
                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                          type="text"
                          value={addAssetQuery}
                          onChange={async (e) => {
                            const q = e.target.value;
                            setAddAssetQuery(q);
                            if (q.length < 2) { setAddAssetResults([]); return; }
                            setAddAssetSearching(true);
                            try {
                              const results = await dataSourceService.searchSecurities(q, 8);
                              setAddAssetResults(results);
                            } catch { setAddAssetResults([]); }
                            finally { setAddAssetSearching(false); }
                          }}
                          placeholder="Search by company name or ticker..."
                          className="w-full h-9 pl-9 pr-8 text-sm bg-surface border border-neutral-200 rounded-lg transition-all"
                          autoFocus
                        />
                        {addAssetSearching && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                          </div>
                        )}
                      </div>
                      {addAssetResults.length > 0 && (
                        <div className="mt-2 border border-neutral-200 rounded-lg max-h-60 overflow-y-auto divide-y divide-neutral-100">
                          {addAssetResults.map(sec => {
                            const alreadyExists = allAssets.some(a => a.dataUid === sec.uid);
                            return (
                              <button
                                key={sec.uid}
                                type="button"
                                disabled={alreadyExists || addingAsset}
                                onClick={() => {
                                  // Create a temporary local-only asset (NOT saved to database)
                                  const tempAsset: Asset = {
                                    id: `temp-${sec.uid}-${Date.now()}`,
                                    name: sec.name,
                                    dataUid: sec.uid,
                                    type: sec.type.toLowerCase() as 'company' | 'fund' | 'property' | 'other',
                                    sector: sec.sector || '',
                                    status: 'active',
                                  };
                                  setLocalAssets(prev => [...prev, tempAsset]);
                                  setMemberAssets(prev => {
                                    const updated = { ...prev };
                                    for (const wId of selectedWorkers) {
                                      updated[wId] = [...(updated[wId] || []), tempAsset.id];
                                    }
                                    return updated;
                                  });
                                  setAddAssetQuery('');
                                  setAddAssetResults([]);
                                }}
                                className="w-full px-3 py-2.5 text-left hover:bg-neutral-50 transition-colors disabled:opacity-50"
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <span className="font-medium text-neutral-900 text-sm">{sec.name}</span>
                                    {sec.sector && <span className="text-xs text-neutral-500 ml-2">{sec.sector}</span>}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <code className="text-xs font-mono text-primary-600">{sec.uid}</code>
                                    {alreadyExists && <span className="text-xs text-neutral-400">Added</span>}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {localAssets.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-neutral-200">
                          <p className="text-xs text-neutral-500 mb-2">Newly added ({localAssets.length})</p>
                          <div className="space-y-1">
                            {localAssets.map(a => (
                              <div key={a.id} className="flex items-center justify-between text-sm">
                                <span className="text-neutral-900">{a.name}</span>
                                <span className="text-xs text-success-600">Added</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {loadingCoverage ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : (
                  <>
                    {/* Search + filters row */}
                    <div className="flex items-center gap-2 mb-3">
                      <div className="relative flex-1">
                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                          type="text"
                          value={coverageSearch}
                          onChange={(e) => setCoverageSearch(e.target.value)}
                          placeholder="Search by ticker, name, or sector..."
                          className="w-full h-9 pl-9 pr-8 text-sm bg-surface border border-neutral-200 rounded-lg transition-all"
                        />
                        {coverageSearch && (
                          <button
                            onClick={() => setCoverageSearch('')}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                      {coverageCategories.length > 0 && (
                        <select
                          value={coverageFilter}
                          onChange={(e) => setCoverageFilter(e.target.value)}
                          className="h-9 px-3 text-sm bg-surface border border-neutral-200 rounded-lg transition-all"
                        >
                          <option value="all">All sectors ({assetView.size})</option>
                          {coverageCategories.map(cat => {
                            const count = Array.from(assetView.values()).filter(v => v.asset.sector === cat).length;
                            return <option key={cat} value={cat}>{cat} ({count})</option>;
                          })}
                        </select>
                      )}
                    </div>

                    {/* Select/Deselect + Add + count */}
                    <div className="flex items-center gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => {
                          const allEnabled = filteredCoverageAssets.every(([id]) => isAssetEnabled(id));
                          for (const [assetId] of filteredCoverageAssets) {
                            const entry = assetView.get(assetId);
                            if (!entry) continue;
                            const enabled = isAssetEnabled(assetId);
                            if (allEnabled && enabled) toggleAssetForAll(assetId);
                            if (!allEnabled && !enabled) toggleAssetForAll(assetId);
                          }
                        }}
                        className="h-7 px-2.5 text-xs font-medium text-neutral-700 bg-neutral-100 border border-neutral-200 rounded hover:bg-neutral-50 transition-colors"
                      >
                        {filteredCoverageAssets.every(([id]) => isAssetEnabled(id)) ? 'Deselect All' : 'Select All'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAddAsset(true)}
                        className="h-7 px-2.5 text-xs font-medium text-primary-600 border border-primary-200 rounded hover:bg-primary-50 transition-colors"
                      >
                        + Add New
                      </button>
                      <span className="text-xs text-neutral-400 ml-auto">
                        {filteredCoverageAssets.filter(([id]) => isAssetEnabled(id)).length} of {filteredCoverageAssets.length} selected
                      </span>
                    </div>

                    {/* Asset table */}
                    {filteredCoverageAssets.length > 0 ? (
                      <div className="border border-neutral-200 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-neutral-50 border-b border-neutral-200">
                              <th className="w-10 py-2.5 px-3">
                                <input
                                  type="checkbox"
                                  checked={filteredCoverageAssets.every(([id]) => isAssetEnabled(id))}
                                  onChange={() => {
                                    const allEnabled = filteredCoverageAssets.every(([id]) => isAssetEnabled(id));
                                    for (const [assetId] of filteredCoverageAssets) {
                                      const enabled = isAssetEnabled(assetId);
                                      if (allEnabled && enabled) toggleAssetForAll(assetId);
                                      if (!allEnabled && !enabled) toggleAssetForAll(assetId);
                                    }
                                  }}
                                  className="w-4 h-4 rounded border-neutral-300 text-primary-600 cursor-pointer"
                                />
                              </th>
                              <SortTh field="name" label="Asset" sort={coverageSort} onSort={(f) => { toggleSortFor(coverageSort, setCoverageSort, f); setCoveragePage(0); }} className="text-center" />
                              <SortTh field="ticker" label="Ticker" sort={coverageSort} onSort={(f) => { toggleSortFor(coverageSort, setCoverageSort, f); setCoveragePage(0); }} className="text-center" />
                              <SortTh field="sector" label="Sector" sort={coverageSort} onSort={(f) => { toggleSortFor(coverageSort, setCoverageSort, f); setCoveragePage(0); }} className="text-center" />
                              <SortTh field="covering" label="Covering" sort={coverageSort} onSort={(f) => { toggleSortFor(coverageSort, setCoverageSort, f); setCoveragePage(0); }} className="w-20 text-center" />
                              <th className="w-10 py-2.5 px-3"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-neutral-100">
                            {(() => {
                              const sorted = [...filteredCoverageAssets].sort((a, b) => {
                                let cmp = 0;
                                switch (coverageSort.field) {
                                  case 'name': cmp = a[1].asset.name.localeCompare(b[1].asset.name); break;
                                  case 'ticker': cmp = (a[1].asset.dataUid || '').localeCompare(b[1].asset.dataUid || ''); break;
                                  case 'sector': cmp = (a[1].asset.sector || '').localeCompare(b[1].asset.sector || ''); break;
                                  case 'covering': {
                                    const ca = selectedWorkers.filter(wId => (memberCoverageMap[wId] || []).some(x => x.id === a[0])).length;
                                    const cb = selectedWorkers.filter(wId => (memberCoverageMap[wId] || []).some(x => x.id === b[0])).length;
                                    cmp = ca - cb; break;
                                  }
                                }
                                return coverageSort.dir === 'asc' ? cmp : -cmp;
                              });
                              return sorted.slice(coveragePage * PAGE_SIZE, (coveragePage + 1) * PAGE_SIZE);
                            })().map(([assetId, { asset }]) => {
                              const enabled = isAssetEnabled(assetId);
                              // Check if any selected analyst actually covers this asset
                              const coveringCount = selectedWorkers.filter(wId => {
                                const cov = memberCoverageMap[wId] || [];
                                return cov.some(a => a.id === assetId);
                              }).length;
                              const isFromCoverage = coveringCount > 0;
                              return (
                                <tr
                                  key={assetId}
                                  onClick={() => toggleAssetForAll(assetId)}
                                  className={`cursor-pointer transition-colors ${enabled ? 'hover:bg-neutral-50' : 'opacity-40'}`}
                                >
                                  <td className="w-10 py-2.5 px-3">
                                    <input
                                      type="checkbox"
                                      checked={enabled}
                                      readOnly
                                      className="w-4 h-4 rounded border-neutral-300 text-primary-600 shrink-0 cursor-pointer"
                                    />
                                  </td>
                                  <td className="py-2.5 px-3 font-medium text-neutral-900">{asset.name}</td>
                                  <td className="py-2.5 px-3 text-neutral-500 font-mono text-xs">{asset.dataUid || '—'}</td>
                                  <td className="py-2.5 px-3 text-neutral-500">{asset.sector || asset.type}</td>
                                  <td className="w-20 py-2.5 px-3 text-center text-neutral-400">
                                    {coveringCount > 0 ? coveringCount : '—'}
                                  </td>
                                  <td className="w-10 py-2.5 px-3 text-center">
                                    {!isFromCoverage ? (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setMemberAssets(prev => {
                                            const updated = { ...prev };
                                            for (const wId of selectedWorkers) {
                                              updated[wId] = (updated[wId] || []).filter(id => id !== assetId);
                                            }
                                            return updated;
                                          });
                                          setLocalAssets(prev => prev.filter(a => a.id !== assetId));
                                          setRemovedAssetIds(prev => new Set(prev).add(assetId));
                                        }}
                                        className="text-neutral-300 hover:text-danger-500 transition-colors"
                                        title="Remove"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      </button>
                                    ) : null}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <Pagination total={filteredCoverageAssets.length} page={coveragePage} setPage={setCoveragePage} />
                      </div>
                    ) : (
                      <div className="text-center py-6 text-neutral-500 text-sm border border-neutral-200 rounded-lg">
                        {coverageSearch ? 'No matching assets' : 'No assets available. Add assets in the Coverage page first.'}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Step 3: Select Workflows */}
            {currentStepKey === 'workflows' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-medium text-neutral-900">Select Workflows</h3>
                  {publishedWorkflows.length > 0 && (
                    <button
                      onClick={toggleSelectAllWorkflows}
                      className="h-7 px-2.5 text-xs font-medium text-neutral-700 bg-neutral-100 border border-neutral-200 rounded hover:bg-neutral-50 transition-colors"
                    >
                      {selectedWorkflows.length === publishedWorkflows.length ? 'Deselect All' : 'Select All'}
                    </button>
                  )}
                </div>
                <p className="text-sm text-neutral-500 mb-4">
                  Choose workflows to assign to the selected team members
                </p>
                <div className="border border-neutral-200 rounded-lg overflow-hidden">
                  {(() => {
                    const withSteps = publishedWorkflows.map(w => ({
                      ...w,
                      _steps: w.flowData?.nodes?.filter((n: any) => n.id !== 'start' && n.type !== 'output').length || 0,
                    }));
                    const sorted = [...withSteps].sort((a, b) => {
                      let cmp = 0;
                      switch (workflowSort.field) {
                        case 'name': cmp = a.name.localeCompare(b.name); break;
                        case 'steps': cmp = a._steps - b._steps; break;
                        case 'duration': cmp = (a.expectedDurationDays ?? 0) - (b.expectedDurationDays ?? 0); break;
                      }
                      return workflowSort.dir === 'asc' ? cmp : -cmp;
                    });
                    const paged = sorted.slice(workflowPage * PAGE_SIZE, (workflowPage + 1) * PAGE_SIZE);
                    return (
                      <>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-neutral-50 border-b border-neutral-200">
                        <th className="w-10 py-2.5 px-3">
                          <input
                            type="checkbox"
                            checked={selectedWorkflows.length === publishedWorkflows.length && publishedWorkflows.length > 0}
                            onChange={toggleSelectAllWorkflows}
                            className="w-4 h-4 rounded border-neutral-300 text-primary-600 cursor-pointer"
                          />
                        </th>
                        <SortTh field="name" label="Workflow" sort={workflowSort} onSort={(f) => { toggleSortFor(workflowSort, setWorkflowSort, f); setWorkflowPage(0); }} className="text-center" />
                        <SortTh field="steps" label="Steps" sort={workflowSort} onSort={(f) => { toggleSortFor(workflowSort, setWorkflowSort, f); setWorkflowPage(0); }} className="w-16 text-center" />
                        <SortTh field="duration" label="Duration" sort={workflowSort} onSort={(f) => { toggleSortFor(workflowSort, setWorkflowSort, f); setWorkflowPage(0); }} className="w-20 text-center" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                      {paged.map((workflow) => {
                        const selected = selectedWorkflows.includes(workflow.id);
                        return (
                          <tr
                            key={workflow.id}
                            onClick={() => toggleSelection(workflow.id, selectedWorkflows, setSelectedWorkflows)}
                            className={`cursor-pointer transition-colors ${selected ? 'bg-primary-900/20' : 'hover:bg-neutral-50'}`}
                          >
                            <td className="w-10 py-3 px-3">
                              <input
                                type="checkbox"
                                checked={selected}
                                readOnly
                                className="w-4 h-4 rounded border-neutral-300 text-primary-600 shrink-0 cursor-pointer"
                              />
                            </td>
                            <td className="py-3 px-3">
                              <p className="font-medium text-neutral-900">{workflow.name}</p>
                              {workflow.description && (
                                <p className="text-xs text-neutral-500 line-clamp-1 mt-0.5">{workflow.description}</p>
                              )}
                            </td>
                            <td className="w-16 py-3 px-3 text-center text-neutral-500">{workflow._steps}</td>
                            <td className="w-20 py-3 px-3 text-center text-neutral-500">
                              {workflow.expectedDurationDays ? `${workflow.expectedDurationDays}d` : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <Pagination total={sorted.length} page={workflowPage} setPage={setWorkflowPage} />
                      </>
                    );
                  })()}
                </div>
                {publishedWorkflows.length === 0 && (
                  <div className="text-center py-8 text-neutral-500">
                    No published workflows available. Publish a workflow first.
                  </div>
                )}
              </div>
            )}

            {/* Review Step */}
            {isReviewStep && (() => {
              const allRows = getAssignmentRows();
              const includedCount = allRows.filter((_, i) => !excludedRows.has(i)).length;
              const filteredRows = allRows.map((row, i) => ({ ...row, idx: i })).filter(({ workerId, assetId, workflowId }) => {
                if (!reviewSearch) return true;
                const q = reviewSearch.toLowerCase();
                const member = teamMembers.find(m => m.id === workerId);
                const asset = assetId ? allCoverageAssets.get(assetId) : null;
                const workflow = workflows.find(w => w.id === workflowId);
                return (
                  (member?.fullName || '').toLowerCase().includes(q) ||
                  (asset?.name || '').toLowerCase().includes(q) ||
                  (asset?.dataUid || '').toLowerCase().includes(q) ||
                  (workflow?.name || '').toLowerCase().includes(q)
                );
              });
              return (
              <div>
                <h3 className="text-lg font-medium text-neutral-900 mb-2">Review Assignments</h3>
                <p className="text-sm text-neutral-500 mb-4">
                  {includedCount} of {allRows.length} assignment{allRows.length !== 1 ? 's' : ''} will be created. Uncheck rows to exclude.
                </p>

                {/* Due Date */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Due Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full h-9 px-3 text-sm bg-surface border border-neutral-200 rounded-lg transition-all"
                  />
                  {dueDate && (() => {
                    const selectedWorkflowObjects = workflows.filter(w => selectedWorkflows.includes(w.id));
                    const durations = selectedWorkflowObjects
                      .map(w => w.expectedDurationDays)
                      .filter((d): d is number => d !== undefined && d > 0);
                    if (durations.length > 0) {
                      const maxDuration = Math.max(...durations);
                      return (
                        <p className="mt-1 text-xs text-neutral-500">
                          Suggested based on workflow duration ({maxDuration} day{maxDuration !== 1 ? 's' : ''})
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>

                {/* Event Date — required when workflow uses specific_date */}
                {needsEventDate && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Event Date <span className="text-danger-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={eventDate}
                      onChange={(e) => setEventDate(e.target.value)}
                      className={`w-full h-9 px-3 text-sm bg-surface border rounded-lg transition-all ${
                        !eventDate ? 'border-danger-300' : 'border-neutral-200'
                      }`}
                    />
                    <p className="mt-1 text-xs text-neutral-500">
                      The date of the event this workflow is anchored to
                    </p>
                  </div>
                )}

                {/* Search */}
                {allRows.length > 3 && (
                  <div className="relative mb-3">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      value={reviewSearch}
                      onChange={(e) => setReviewSearch(e.target.value)}
                      placeholder="Filter by analyst, asset, or workflow..."
                      className="w-full h-9 pl-9 pr-8 text-sm bg-surface border border-neutral-200 rounded-lg transition-all"
                    />
                    {reviewSearch && (
                      <button onClick={() => setReviewSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}

                {/* Assignment table */}
                <div className="border border-neutral-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-neutral-50 border-b border-neutral-200">
                        <th className="w-10 py-2.5 px-3">
                          <input
                            type="checkbox"
                            checked={filteredRows.length > 0 && filteredRows.every(r => !excludedRows.has(r.idx))}
                            onChange={() => {
                              const allIncluded = filteredRows.every(r => !excludedRows.has(r.idx));
                              setExcludedRows(prev => {
                                const next = new Set(prev);
                                for (const r of filteredRows) {
                                  if (allIncluded) next.add(r.idx); else next.delete(r.idx);
                                }
                                return next;
                              });
                            }}
                            className="w-4 h-4 rounded border-neutral-300 text-primary-600 cursor-pointer"
                          />
                        </th>
                        <SortTh field="analyst" label="Analyst" sort={reviewSort} onSort={(f) => { toggleSortFor(reviewSort, setReviewSort, f); setReviewPage(0); }} className="text-center" />
                        <SortTh field="asset" label="Asset" sort={reviewSort} onSort={(f) => { toggleSortFor(reviewSort, setReviewSort, f); setReviewPage(0); }} className="text-center" />
                        <SortTh field="covered" label="Covered" sort={reviewSort} onSort={(f) => { toggleSortFor(reviewSort, setReviewSort, f); setReviewPage(0); }} className="w-20 text-center" />
                        <SortTh field="workflow" label="Workflow" sort={reviewSort} onSort={(f) => { toggleSortFor(reviewSort, setReviewSort, f); setReviewPage(0); }} className="text-center" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                  {[...filteredRows].sort((a, b) => {
                    let cmp = 0;
                    switch (reviewSort.field) {
                      case 'analyst': {
                        const ma = teamMembers.find(m => m.id === a.workerId);
                        const mb = teamMembers.find(m => m.id === b.workerId);
                        cmp = (ma?.fullName || '').localeCompare(mb?.fullName || ''); break;
                      }
                      case 'asset': {
                        const aa = a.assetId ? allCoverageAssets.get(a.assetId) : null;
                        const ab = b.assetId ? allCoverageAssets.get(b.assetId) : null;
                        cmp = (aa?.name || '').localeCompare(ab?.name || ''); break;
                      }
                      case 'covered': {
                        const ca = a.assetId ? (memberCoverageMap[a.workerId] || []).some(x => x.id === a.assetId) ? 1 : 0 : 0;
                        const cb = b.assetId ? (memberCoverageMap[b.workerId] || []).some(x => x.id === b.assetId) ? 1 : 0 : 0;
                        cmp = ca - cb; break;
                      }
                      case 'workflow': {
                        const wa = workflows.find(w => w.id === a.workflowId);
                        const wb = workflows.find(w => w.id === b.workflowId);
                        cmp = (wa?.name || '').localeCompare(wb?.name || ''); break;
                      }
                    }
                    return reviewSort.dir === 'asc' ? cmp : -cmp;
                  }).slice(reviewPage * PAGE_SIZE, (reviewPage + 1) * PAGE_SIZE).map(({ workerId, assetId, workflowId, idx }) => {
                    const member = teamMembers.find(m => m.id === workerId);
                    const asset = assetId ? allCoverageAssets.get(assetId) : null;
                    const workflow = workflows.find(w => w.id === workflowId);
                    const included = !excludedRows.has(idx);
                    const workerCoverage = memberCoverageMap[workerId] || [];
                    const isCovered = assetId ? workerCoverage.some(a => a.id === assetId) : false;
                    return (
                      <tr
                        key={idx}
                        onClick={() => setExcludedRows(prev => {
                          const next = new Set(prev);
                          if (next.has(idx)) next.delete(idx); else next.add(idx);
                          return next;
                        })}
                        className={`cursor-pointer transition-colors ${included ? 'hover:bg-neutral-50' : 'opacity-40'}`}
                      >
                        <td className="w-10 py-2.5 px-3">
                          <input
                            type="checkbox"
                            checked={included}
                            readOnly
                            className="w-4 h-4 rounded border-neutral-300 text-primary-600 shrink-0 cursor-pointer"
                          />
                        </td>
                        <td className="py-2.5 px-3 font-medium text-neutral-900">{member?.fullName || 'Unknown'}</td>
                        <td className="py-2.5 px-3 text-neutral-600">{asset?.name || '—'}</td>
                        <td className="w-20 py-2.5 px-3 text-center">
                          {isCovered
                            ? <span className="text-success-500 text-xs font-medium">Yes</span>
                            : <span className="text-neutral-400 text-xs">No</span>
                          }
                        </td>
                        <td className="py-2.5 px-3 text-neutral-500">{workflow?.name || 'Unknown'}</td>
                      </tr>
                    );
                  })}
                    </tbody>
                  </table>
                  <Pagination total={filteredRows.length} page={reviewPage} setPage={setReviewPage} />
                </div>

              </div>
              );
            })()}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-neutral-200 flex justify-between items-center">
            <Button
              variant="outline"
              size="sm"
              onClick={step <= 1 ? onClose : () => setStep(getPrevStep())}
            >
              {step <= 1 ? 'Cancel' : 'Back'}
            </Button>
            <div className="flex items-center gap-3">
              {currentStepKey === 'team' && (
                <span className="text-sm text-neutral-500">
                  {selectedWorkers.length} selected
                </span>
              )}
              {isAssetsStep && (
                <span className="text-sm text-neutral-500">
                  {Object.values(memberAssets).reduce((sum, ids) => sum + ids.length, 0)} assets selected
                </span>
              )}
              {currentStepKey === 'workflows' && (
                <span className="text-sm text-neutral-500">
                  {selectedWorkflows.length} selected
                </span>
              )}
              <Button
                variant="primary"
                size="sm"
                onClick={isReviewStep ? handleSubmit : () => setStep(getNextStep())}
                disabled={!canProceed()}
              >
                {isReviewStep ? 'Create Assignments' : 'Next'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewAssignmentModal;
